(() => {
  if (window.KaawaCartCheckout) return;
  const attribute = 'kaawa_legal_consent';
  const transport = window.fetch.bind(window);
  class CartCheckoutCoordinator {
    state = null;
    pending = 0;
    revision = 0;
    verifying = false;
    mutationError = null;
    noteFailed = false;
    changed = false;
    desired = null;
    acceptedFingerprint = null;
    intent = 0;
    tail = Promise.resolve();
    quantities = new Map();
    noteDraft = null;
    noteVersion = 0;
    remote = new Map();
    operations = new Set();
    syncing = false;

    constructor() {
      try { this.veto = localStorage.getItem('kaawa-consent-withdrawal'); } catch { this.veto = null; }
      window.addEventListener('storage', event => {
        if (event.key !== 'kaawa-consent-withdrawal') return;
        this.veto = event.newValue;
        this.revision++;
        this.render();
        this.refresh();
      });
      this.channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('kaawa-cart-checkout-v1') : null;
      this.channel?.addEventListener('message', ({ data }) => {
        if (data?.type === 'consent' && data.fingerprint === this.state?.fingerprint) {
          this.desired = data.checked;
          this.acceptedFingerprint = data.checked ? data.fingerprint : null;
          this.render();
          return;
        }
        if (!data?.id) return;
        this.revision++;
        if (data.type === 'start') {
          if (data.material && !this.remote.has(data.id)) this.invalidate();
          this.remote.set(data.id, Date.now());
        }
        if (data.type === 'end') {
          this.remote.delete(data.id);
        }
        this.render();
        this.refresh();
      });
      document.addEventListener('submit', event => this.submit(event), true);
      document.addEventListener('input', event => {
        if (event.target.matches('cart-note textarea')) this.setNote(event.target.value);
        if (event.target.matches('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')) {
          const key = event.target.closest('[data-cart-line-key]')?.dataset.cartLineKey;
          if (key) this.quantities.set(key, event.target.value);
          this.invalidate();
          this.revision++;
          this.render();
        }
      }, true);
      new MutationObserver(() => this.render()).observe(document.documentElement, { childList: true, subtree: true });
      window.addEventListener('focus', () => this.refresh());
      window.addEventListener('pageshow', () => this.refresh());
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.refresh();
      });
      if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.revision++;
        this.refresh();
      });
      setInterval(() => {
        this.poll();
        if (!document.hidden && (document.querySelector('#CartPage-CheckoutForm') || document.querySelector('cart-drawer.active'))) this.refresh();
      }, 5000);
    }

    poll() {
      for (const id of this.operations) this.channel?.postMessage({ type: 'start', id });
      for (const [id, seen] of this.remote) {
        if (Date.now() - seen < 30000) continue;
        this.remote.delete(id);
        this.revision++;
      }
      this.render();
    }

    get busy() { return this.syncing || this.pending > 0 || this.quantities.size > 0 || this.noteDraft !== null || this.remote.size > 0; }
    get valid() {
      return this.state?.count > 0 && this.state.invalid.length === 0 && this.veto !== this.state.fingerprint &&
        (this.desired === null ? this.state.consent === this.state.fingerprint : this.desired && this.acceptedFingerprint === this.state.fingerprint);
    }
    get ready() { return this.valid && !this.busy; }
    request(...args) { return transport(...args); }
    cartUrl() { return window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`; }
    withdrawal(fingerprint) {
      this.veto = fingerprint || null;
      try {
        if (this.veto) localStorage.setItem('kaawa-consent-withdrawal', this.veto);
        else localStorage.removeItem('kaawa-consent-withdrawal');
      } catch {}
    }

    async readState() {
      const url = new URL(this.cartUrl(), location.origin);
      url.pathname += '.js';
      url.searchParams.set('_p3b', `${Date.now()}-${this.revision}`);
      const response = await transport(url.href, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart verification failed');
      return this.stateFromCart(await response.json());
    }

    async stateFromCart(cart) {
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) ||
        typeof cart.currency !== 'string' || !Number.isInteger(cart.total_price) || cart.items.some(item =>
          typeof item.key !== 'string' || !Number.isInteger(item.quantity) || !Number.isInteger(item.final_line_price) ||
          !Number.isInteger(item.product_id) || typeof item.product_title !== 'string' || typeof item.url !== 'string' || !/^\/(?!\/)/.test(item.url))) {
        throw new Error('Invalid cart verification');
      }
      const transaction = `kaawa-v1|${cart.currency}|${cart.total_price}|` + [...cart.items].sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
        .map(item => `${item.key}:${item.quantity}:${item.final_line_price};`).join('');
      const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(transaction));
      const fingerprint = 'v1:' + [...new Uint8Array(hash)].map(byte => byte.toString(16).padStart(2, '0')).join('');
      const lines = cart.items.map(item => ({ ...item, grind: window.KaawaCartGrind.normalize(item) }));
      return { fingerprint, consent: String(cart.attributes?.[attribute] || '').trim(), count: cart.item_count, note: cart.note || '', lines,
        invalid: lines.filter(item => item.grind === 'invalid').map(item => ({ key: item.key, title: item.product_title, url: item.url })) };
    }

    async write(data) {
      const response = await transport(window.routes?.cart_update_url || `${this.cartUrl()}/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Cart save failed');
      const result = await response.json();
      if (result.errors || result.status) throw new Error('Cart save rejected');
      return result;
    }

    invalidate() {
      this.desired = false;
      this.acceptedFingerprint = null;
      this.intent++;
      this.changed = true;
      this.withdrawal(this.state?.fingerprint);
      this.render();
    }

    adoptState(state) {
      const changed = this.state && this.state.fingerprint !== state.fingerprint;
      this.state = state;
      // A fresh authoritative cart supersedes every historical request failure.
      this.mutationError = null;
      for (const error of document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors')) error.textContent = '';
      if (this.noteDraft === state.note) { this.noteDraft = null; this.noteFailed = false; }
      if (changed) this.invalidate();
      return changed;
    }

    async clearConsent() {
      await this.write({ attributes: { [attribute]: '' } });
    }

    enqueue(task, { blocking = true, material = false, mutation = null } = {}) {
      const id = crypto.randomUUID();
      if (blocking) {
        this.pending++;
        this.revision++;
        this.operations.add(id);
        this.channel?.postMessage({ type: 'start', id, material });
      }
      this.render();
      const execute = async () => {
        const result = await task();
        if (blocking) {
          try {
            const changed = this.adoptState(await this.readState());
            if ((changed || !this.surfacesMatch()) && !this.quantities.size) await this.refreshSurfaces();
          } catch {
            // Verification/section failures are not customer mutation failures.
            // Do not reuse pre-mutation line readiness if cart retrieval failed.
            if (material) this.state = null;
          }
        }
        return result;
      };
      const work = this.tail.then(() => navigator.locks ? navigator.locks.request('kaawa-cart-checkout', execute) : execute()).catch(error => {
        if (mutation) this.mutationError = { id, kind: mutation };
        throw error;
      }).finally(() => {
        if (blocking) {
          this.pending--;
          this.operations.delete(id);
          this.channel?.postMessage({ type: 'end', id });
        }
        this.render();
      });
      this.tail = work.catch(() => {});
      return work;
    }

    mutation(task, key, value) {
      this.invalidate();
      return this.enqueue(async () => {
        try { await this.clearConsent(); } catch {}
        const result = await task();
        return result;
      }, { material: true, mutation: 'cart' }).finally(() => this.releaseQuantity(key, value));
    }

    releaseQuantity(key, value) {
      if (this.quantities.get(key) === String(value)) this.quantities.delete(key);
      this.render();
    }

    consent(checked) {
      const target = this.state?.fingerprint;
      const intent = ++this.intent;
      this.desired = checked;
      this.acceptedFingerprint = checked ? target : null;
      this.withdrawal(checked ? null : target);
      this.revision++;
      this.changed = false;
      if (checked) {
        // The rejected edit already restored the server quantity; clear only our stale constraint.
        for (const input of document.querySelectorAll('cart-drawer-items input[name="updates[]"]')) {
          if (input.value === input.defaultValue && input.validity.customError && input.validationMessage === input.kaawaQuantityValidation) {
            input.setCustomValidity('');
            delete input.kaawaQuantityValidation;
          }
        }
      }
      this.channel?.postMessage({ type: 'consent', fingerprint: target, checked });
      this.render();
      return this.enqueue(async () => {
        if (intent !== this.intent) return;
        const current = await this.readState();
        if (checked && (current.fingerprint !== target || current.invalid.length || current.count === 0)) {
          const changed = this.adoptState(current);
          this.invalidate();
          if (changed && !this.quantities.size) await this.refreshSurfaces();
          await this.clearConsent();
          return;
        }
        if (intent !== this.intent) return;
        this.adoptState(current);
        if (intent !== this.intent) return;
        const value = checked ? target : '';
        await this.write({ attributes: { [attribute]: value } });
      }, { blocking: false }).catch(() => {});
    }

    setNote(value) {
      this.noteDraft = value;
      this.noteFailed = false;
      this.noteVersion++;
      this.revision++;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => this.saveNote(), window.ON_CHANGE_DEBOUNCE_TIMER || 300);
      this.render();
    }

    saveNote() {
      if (this.noteDraft === null) return;
      const value = this.noteDraft, version = this.noteVersion;
      return this.enqueue(async () => {
        // The update response is authoritative acknowledgement of this write.
        // A later GET/section failure must not turn a saved note into a failed mutation.
        const saved = await this.stateFromCart(await this.write({ note: value }));
        if (saved.note !== value) throw new Error('Note persistence not confirmed');
        this.adoptState(saved);
        if (version === this.noteVersion) this.noteDraft = null;
        this.noteFailed = false;
      }, { mutation: 'note' }).catch(() => { this.noteFailed = true; this.render(); });
    }

    async refresh() {
      if (this.pending || this.remote.size || this.verifying) return;
      this.verifying = true;
      const revision = this.revision;
      this.render();
      try {
        const state = await this.readState();
        if (revision !== this.revision) return;
        const changed = this.adoptState(state);
        if ((changed || !this.surfacesMatch()) && !this.quantities.size) await this.refreshSurfaces();
        if (state.consent && state.consent !== state.fingerprint && this.desired !== true) {
          await this.enqueue(() => this.clearConsent(), { blocking: false }).catch(() => {});
        }
      } catch {
        // Keep the rendered cart usable if a background refresh is unavailable.
      } finally {
        this.verifying = false;
        this.render();
        if (revision !== this.revision) queueMicrotask(() => this.refresh());
      }
    }

    async refreshSurfaces() {
      this.syncing = true;
      this.render();
      try {
      const sections = [{ name: 'cart-icon-bubble', selector: '.shopify-section', id: 'cart-icon-bubble' }];
      if (document.querySelector('cart-drawer')) sections.push({ name: 'cart-drawer', drawer: true });
      for (const [id, selector] of [['main-cart-items', '.js-contents'], ['main-cart-footer', '.cart__blocks']]) {
        const element = document.getElementById(id);
        if (element) sections.push({ name: element.dataset.id, id, selector });
      }
      const url = new URL(this.cartUrl(), location.origin);
      url.searchParams.set('sections', sections.map(section => section.name).join(','));
      url.searchParams.set('_p3b', String(Date.now()));
      const response = await transport(url.href, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart refresh failed');
      const rendered = await response.json();
      for (const section of sections) {
        const html = rendered[section.name];
        if (!html) throw new Error('Cart section missing');
        if (section.drawer) document.querySelector('cart-drawer')?.refreshContents(html);
        else {
          const target = document.getElementById(section.id);
          if (!target) continue;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (!source) throw new Error('Cart section invalid');
          (target.querySelector(section.selector) || target).innerHTML = source.innerHTML;
        }
      }
      document.querySelector('cart-items')?.classList.toggle('is-empty', this.state.count === 0);
      document.querySelector('#main-cart-footer')?.classList.toggle('is-empty', this.state.count === 0);
      } finally { this.syncing = false; this.render(); }
    }

    surfacesMatch() {
      const expected = this.state.lines.map(item => `${item.key}:${item.quantity}`).sort().join('|');
      return [...document.querySelectorAll('cart-items, cart-drawer-items')].every(surface =>
        [...surface.querySelectorAll('[data-cart-line-key]')].map(row =>
          `${row.dataset.cartLineKey}:${row.querySelector('input[name="updates[]"]')?.value}`).sort().join('|') === expected);
    }

    retry() {
      if (this.noteDraft !== null) return this.saveNote();
      return this.refresh();
    }

    render() {
      for (const textarea of document.querySelectorAll('cart-note textarea')) {
        const value = this.noteDraft ?? this.state?.note;
        if (value !== undefined && textarea.value !== value) textarea.value = value;
      }
      for (const control of document.querySelectorAll('cart-checkout')) control.update?.(this);
    }

    blocked(control) {
      if (!control) return;
      const checkbox = control.querySelector('input[type="checkbox"]');
      checkbox.setAttribute('aria-invalid', 'true');
      const target = this.state?.invalid.length ? control.querySelector('[data-checkout-corrections] a') : checkbox;
      target?.focus();
    }

    submit(event) {
      const form = event.target, submitter = event.submitter;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id === 'cart' && this.busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.render();
        return;
      }
      const control = [...document.querySelectorAll('cart-checkout')].find(item => item.dataset.formId === form.id);
      if (!form.hasAttribute('data-cart-checkout-form') && !(submitter?.name === 'checkout' && new URL(form.action).pathname.endsWith('/cart'))) return;
      const checkbox = control?.querySelector('input[type="checkbox"]');
      if (submitter?.name === 'update' && control) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return this.consent(checkbox.checked);
      }
      if (!this.ready || !checkbox?.checked || !form.isConnected) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return this.blocked(control);
      }
      if (!submitter) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setTimeout(() => {
          if (form.isConnected && this.ready && checkbox.checked) form.requestSubmit(control.querySelector('[data-checkout-button]'));
        });
        return;
      }
      // Let Shopify's original submit proceed; exclude stale drawer quantity fields.
      const inputs = [...form.elements].filter(input => input.name === 'updates[]' && !input.disabled);
      inputs.forEach(input => { input.disabled = true; });
      setTimeout(() => inputs.forEach(input => { input.disabled = false; }));
    }
  }

  const coordinator = window.KaawaCartCheckout = new CartCheckoutCoordinator();
  class CartCheckout extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('noscript').forEach(element => element.remove());
      // Never restore readiness from a section's stale Liquid snapshot.
      coordinator.mutationError = null;
      if (!this.bound) {
        this.bound = true;
        this.querySelector('input[type="checkbox"]').addEventListener('change', event => coordinator.consent(event.target.checked));
        this.querySelector('[data-checkout-retry]').addEventListener('click', () => coordinator.retry());
      }
      this.querySelector('[data-checkout-save]').hidden = true;
      this.update(coordinator);
      coordinator.refresh();
    }

    update(cart) {
      const checkbox = this.querySelector('input[type="checkbox"]');
      checkbox.checked = cart.desired ?? Boolean(cart.valid && cart.veto !== cart.state?.fingerprint);
      if (cart.state) checkbox.value = cart.state.fingerprint;
      const button = this.querySelector('[data-checkout-button]');
      button.disabled = !cart.ready;
      this.querySelector('[data-checkout-retry]').hidden = !(cart.noteFailed && cart.noteDraft !== null);
      const status = this.querySelector('[data-checkout-status]');
      const confirmed = cart.valid && cart.desired !== false && cart.veto !== cart.state?.fingerprint;
      const message = cart.mutationError ? this.dataset.failed : cart.busy || !cart.state ? this.dataset.saving :
        cart.state?.invalid.length ? this.dataset.grind : confirmed ? this.dataset.ready : cart.changed ? this.dataset.changed : this.dataset.required;
      if (status.textContent !== message) status.textContent = message;
      status.classList.toggle('visually-hidden', !cart.mutationError);
      status.dataset.error = String(Boolean(cart.mutationError) || (!cart.ready && !cart.busy && !cart.verifying));
      if (cart.ready) checkbox.removeAttribute('aria-invalid');
      const grind = this.querySelector('[data-checkout-grind]');
      const invalid = cart.state?.invalid || [];
      grind.hidden = invalid.length === 0;
      const list = this.querySelector('[data-checkout-corrections]');
      const signature = JSON.stringify(invalid);
      if (list.dataset.signature !== signature) {
        list.dataset.signature = signature;
        list.replaceChildren(...invalid.map(item => {
          const li = document.createElement('li'), link = document.createElement('a');
          link.href = item.url;
          link.textContent = item.title;
          li.append(link);
          return li;
        }));
      }
    }
  }
  customElements.define('cart-checkout', CartCheckout);

  function operation(input, init = {}) {
    init = init || {};
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.origin);
    const method = init.method || input.method || 'GET';
    if (url.origin !== location.origin || !/^(POST|PUT|PATCH|DELETE)$/i.test(method)) return null;
    const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/);
    if (!match) return null;
    let body = init.body;
    if (typeof body === 'string') {
      try { body = JSON.parse(body); } catch { body = new URLSearchParams(body); }
    }
    const knownBody = body instanceof FormData || body instanceof URLSearchParams || body?.constructor === Object;
    const keys = body instanceof FormData || body instanceof URLSearchParams ? [...body.keys()] : Object.keys(body || {});
    return { material: match[1] !== 'update' || !knownBody || keys.some(key => key === 'updates' || key.startsWith('updates[')), note: keys.includes('note') };
  }
  window.fetch = (input, init) => {
    const change = operation(input, init);
    if (!change) return transport(input, init);
    const task = async () => {
      const response = await transport(input, init);
      if (!response.ok && (change.material || change.note)) coordinator.mutationError = { kind: change.note ? 'note' : 'cart' };
      return response;
    };
    return change.material ? coordinator.mutation(task) : coordinator.enqueue(task, { blocking: change.note, mutation: change.note ? 'note' : null });
  };
  const open = XMLHttpRequest.prototype.open, send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    this.kaawaCartRequest = { method, url, asynchronous: args[0] !== false };
    return open.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function(body) {
    const request = this.kaawaCartRequest;
    const change = request && operation(request.url, { method: request.method, body });
    if (!change) return send.call(this, body);
    const task = () => new Promise((resolve, reject) => {
      this.addEventListener('loadend', () => this.status >= 200 && this.status < 300 ? resolve() : reject(new Error('Cart request failed')), { once: true });
      send.call(this, body);
    });
    if (!request.asynchronous) {
      if (change.material) coordinator.invalidate();
      const result = send.call(this, body);
      if ((change.material || change.note) && (this.status < 200 || this.status >= 300)) coordinator.mutationError = { kind: change.note ? 'note' : 'cart' };
      coordinator.refresh();
      return result;
    }
    (change.material ? coordinator.mutation(task) : coordinator.enqueue(task, { blocking: change.note, mutation: change.note ? 'note' : null })).catch(() => {});
  };
})();
